import { defaultAnswers, } from "./answers";
import { Q10_OPTIONS, Q11_OPTIONS, Q12_OPTIONS, Q13_FREQUENCY_OPTIONS, Q13_OPTIONS, Q13_PERIOD_OPTIONS, Q13_TYPE_OPTIONS, Q14_OPTIONS, Q1_OPTIONS, Q2_OPTIONS, Q3_OPTIONS, Q4_OPTIONS, Q5_OPTIONS, Q6_OPTIONS, Q7_OPTIONS, Q7_RESULT_OPTIONS, Q8_OPTIONS, Q9_OPTIONS, QUESTION_INDEX, } from "./questions";
const findQuestion = (source, questionIndex) => source.find((q) => q.questionIndex === questionIndex);
const firstAnswer = (q) => q?.answers?.[0];
const optionAt = (options, index) => {
    if (index === undefined || index < 0 || index >= options.length)
        return undefined;
    return options[index];
};
const optionsAt = (options, indexes) => {
    if (!indexes)
        return [];
    return indexes
        .map((i) => optionAt(options, i))
        .filter((v) => v !== undefined);
};
/**
 * 問診回答（API レスポンス相当）を意味的な構造体に変換する。
 *
 * 不正な値や未回答が含まれる場合はそのフィールドだけデフォルトに戻すため、
 * 部分的な回答（途中保存）でも安全に扱える。
 *
 * shared_front は api-client に依存しないため、入力は構造的な `RawQuestion[]`
 * として受け取る。patient/clinic それぞれの api-client 型はこの形と互換。
 *
 * 利用側からは `UpneeqInterviewAnswers.parse(...)` として呼ばれる。
 */
export const parse = (source) => {
    if (!source || source.length === 0) {
        return defaultAnswers;
    }
    const next = {
        ...defaultAnswers,
        q5: { ...defaultAnswers.q5 },
        q6: { ...defaultAnswers.q6 },
        q7: { ...defaultAnswers.q7 },
        q8: { ...defaultAnswers.q8 },
        q10: { ...defaultAnswers.q10 },
        q11: { ...defaultAnswers.q11 },
        q12: { ...defaultAnswers.q12 },
        q13: { ...defaultAnswers.q13 },
        q14: { ...defaultAnswers.q14 },
    };
    next.q1 = optionAt(Q1_OPTIONS, firstAnswer(findQuestion(source, QUESTION_INDEX.q1))?.selectNumber?.[0]);
    next.q2 = optionAt(Q2_OPTIONS, firstAnswer(findQuestion(source, QUESTION_INDEX.q2))?.selectNumber?.[0]);
    next.q3 = optionAt(Q3_OPTIONS, firstAnswer(findQuestion(source, QUESTION_INDEX.q3))?.selectNumber?.[0]);
    next.q4 = optionAt(Q4_OPTIONS, firstAnswer(findQuestion(source, QUESTION_INDEX.q4))?.selectNumber?.[0]);
    const q5Answer = firstAnswer(findQuestion(source, QUESTION_INDEX.q5));
    next.q5.selected = optionsAt(Q5_OPTIONS, q5Answer?.selectNumber);
    next.q5.otherText = q5Answer?.comment ?? "";
    const q6Answer = firstAnswer(findQuestion(source, QUESTION_INDEX.q6));
    next.q6.selected = optionsAt(Q6_OPTIONS, q6Answer?.selectNumber);
    const q7Answer = firstAnswer(findQuestion(source, QUESTION_INDEX.q7));
    next.q7.selected = optionAt(Q7_OPTIONS, q7Answer?.selectNumber?.[0]);
    const q7SubAnswer = firstAnswer(q7Answer?.additionalQuestions?.[0]);
    next.q7.results = optionsAt(Q7_RESULT_OPTIONS, q7SubAnswer?.selectNumber);
    const q8Answer = firstAnswer(findQuestion(source, QUESTION_INDEX.q8));
    next.q8.selected = optionsAt(Q8_OPTIONS, q8Answer?.selectNumber);
    next.q8.otherText = q8Answer?.comment ?? "";
    next.q9 = optionAt(Q9_OPTIONS, firstAnswer(findQuestion(source, QUESTION_INDEX.q9))?.selectNumber?.[0]);
    const q10Answer = firstAnswer(findQuestion(source, QUESTION_INDEX.q10));
    next.q10.selected = optionsAt(Q10_OPTIONS, q10Answer?.selectNumber);
    const q11Answer = firstAnswer(findQuestion(source, QUESTION_INDEX.q11));
    next.q11.selected = optionsAt(Q11_OPTIONS, q11Answer?.selectNumber);
    next.q11.otherText = q11Answer?.comment ?? "";
    const q12Answer = firstAnswer(findQuestion(source, QUESTION_INDEX.q12));
    next.q12.selected = optionAt(Q12_OPTIONS, q12Answer?.selectNumber?.[0]);
    // Q12 sub-question ローカル序列: 1=drugName, 2=symptom
    const q12Subs = q12Answer?.additionalQuestions ?? [];
    next.q12.drugName = firstAnswer(q12Subs[0])?.comment ?? "";
    next.q12.symptom = firstAnswer(q12Subs[1])?.comment ?? "";
    const q13Answer = firstAnswer(findQuestion(source, QUESTION_INDEX.q13));
    next.q13.selected = optionAt(Q13_OPTIONS, q13Answer?.selectNumber?.[0]);
    // Q13 sub-question ローカル序列: 1=types, 2=period, 3=frequency
    const q13Subs = q13Answer?.additionalQuestions ?? [];
    next.q13.types = optionsAt(Q13_TYPE_OPTIONS, firstAnswer(q13Subs.find((s) => s.questionIndex === 1))?.selectNumber);
    next.q13.period = optionAt(Q13_PERIOD_OPTIONS, firstAnswer(q13Subs.find((s) => s.questionIndex === 2))
        ?.selectNumber?.[0]);
    next.q13.frequency = optionAt(Q13_FREQUENCY_OPTIONS, firstAnswer(q13Subs.find((s) => s.questionIndex === 3))
        ?.selectNumber?.[0]);
    const q14Answer = firstAnswer(findQuestion(source, QUESTION_INDEX.q14));
    next.q14.selected = optionAt(Q14_OPTIONS, q14Answer?.selectNumber?.[0]);
    next.q14.weeks =
        typeof q14Answer?.period === "number" ? q14Answer.period : undefined;
    return next;
};
